import { PlusOutlined } from "@ant-design/icons";
import { Button, Card, notification, Skeleton } from "antd";
import React, { useEffect, useState } from "react";
import { useQuery } from "react-query";
import HTTP from "../../services/httpService";
import { getUserRole } from "../../services/util";
import CustomHeader from "../common/Header";
import CustomTable from "../common/Table";
import AddEditAlarm from "./AddEditAlarm";
import AlarmHistoryModal from "./AlarmHistoryModal";
import "./Alarms.css";
import column from "./column";

// ─── Constants ────────────────────────────────────────────────────────────────

const ALARMS_EMPTY = {
    staff_id: 1,
    alarm_name: "",
    priority: 1,
    alarm_type: [1],
    alarm_hotel_ids: [],
    alarm_send_to_email: true,
    alarm_send_to_phone: true,
    alarm_email_ids: [],
    alarm_phone_numbers: [],
};

export const ALARM_PRIORITY = [
    { name: "High", value: 1 },
    { name: "Medium", value: 2 },
    { name: "Low", value: 3 },
];

export const ALARM_TYPE = [
    { key: 1, title: "Support Ticket Create" },
    { key: 2, title: "Support Ticket Update" },
    { key: 3, title: "Support Ticket Close" },
    { key: 4, title: "Bad Reviews Feedback" },
];

const ADMIN_ROLES = ["Super Admin", "Admin"];
const ALLOWED_ROLES = [...ADMIN_ROLES, "Support"];

// ─── Component ────────────────────────────────────────────────────────────────

const Alarms = () => {
    const staff_id = localStorage.getItem("x-dashboard-staffid");
    const userRole = getUserRole(localStorage.getItem("userRoleId"));

    const [formData, setFormData] = useState({ staff_id, page: 0, limit: 10 });
    const [totalAlarms, setTotalAlarms] = useState(0);
    const [alarmsData, setAlarmsData] = useState([]);

    const [isVisible, setIsVisible] = useState(false);
    const [alarmItem, setAlarmItem] = useState(ALARMS_EMPTY);

    const [isShowHistory, setIsShowHistory] = useState(false);
    const [historyData, setHistoryData] = useState([]);
    const [totalAlarmsHistory, setTotalAlarmsHistory] = useState(0);

    // ── Queries ──────────────────────────────────────────────────────────────

    const { data: hotelListRes, refetch: fetchHotelList } = useQuery(
        "mapHotels",
        () => HTTP("/dashboard/getMapHotels", { staff_id }),
        { enabled: false },
    );

    const {
        status: alarmsStatus,
        data: alarms,
        refetch: fetchAlarms,
    } = useQuery("alarms", () => HTTP("/alarms/getAlarms", { ...formData }), {
        enabled: false,
    });

    // ── Effects ───────────────────────────────────────────────────────────────

    useEffect(() => {
        fetchAlarms();
        fetchHotelList();
    }, [formData]);

    useEffect(() => {
        if (alarmsStatus === "success") {
            setTotalAlarms(alarms.total);
            setAlarmsData(alarms.data);
        }
    }, [alarmsStatus, alarms]);

    // ── Handlers ─────────────────────────────────────────────────────────────

    const openAlarmModal = (record) => {
        if (!ADMIN_ROLES.includes(userRole)) {
            return notification.error({
                message: "You are not allowed to perform this action.",
            });
        }
        setAlarmItem(record);
        setIsVisible(true);
    };

    const closeModal = () => {
        setIsVisible(false);
        setAlarmItem(ALARMS_EMPTY);
        fetchAlarms();
    };

    const handleTableChange = (pagination, _, sorter) => {
        const { current, pageSize } = pagination;
        const { order, field } = sorter;

        const updates = { page: current - 1, limit: pageSize };

        if (order) {
            updates.sorting = order === "ascend" ? 1 : 2;
            updates.sort_by = field;
        } else {
            const { sorting, sort_by, ...rest } = formData;
            return setFormData({ ...rest, ...updates });
        }

        setFormData((prev) => ({ ...prev, ...updates }));
    };

    const showHistory = async (e) => {
        const alarm_id = e.currentTarget.getAttribute("id");
        const { success, data, message, total } = await HTTP(
            "/alarms/getAlarmsHistory",
            {
                staff_id: 1,
                limit: 10,
                page: 0,
                sort_by: "",
                sorting: 1,
                alarm_id,
            },
        );

        if (success) {
            setHistoryData(data);
            setTotalAlarmsHistory(total);
            setIsShowHistory(true);
        } else {
            notification.error({ message });
        }
    };

    const closeHistoryModal = () => {
        setIsShowHistory(false);
        setHistoryData([]);
        setTotalAlarmsHistory(0);
    };

    // ── Derived ───────────────────────────────────────────────────────────────

    const isReady = alarmsStatus !== "loading" && alarmsStatus !== "idle";

    // ── Render ────────────────────────────────────────────────────────────────

    return (
        <Card className="dashboard-container">
            <CustomHeader
                extraAfter={
                    ALLOWED_ROLES.includes(userRole) && (
                        <Button
                            type="primary"
                            icon={<PlusOutlined />}
                            style={{ marginRight: 5 }}
                            onClick={() => setIsVisible(true)}
                        >
                            Add Alarm
                        </Button>
                    )
                }
            />

            {isReady ? (
                <CustomTable
                    columns={column({
                        openAlarmModal,
                        getAlarms: fetchAlarms,
                        hotelListRes,
                        alarmPriority: ALARM_PRIORITY,
                        alarmType: ALARM_TYPE,
                        showHistory,
                    })}
                    handleTableChange={handleTableChange}
                    tableData={alarmsData}
                    noOfrecords={totalAlarms}
                    rowKey="id"
                    isScroll
                />
            ) : (
                <Skeleton
                    active
                    title={false}
                    paragraph={{ rows: 20, width: 40 }}
                />
            )}

            {isVisible && (
                <AddEditAlarm
                    visible={isVisible}
                    onClose={closeModal}
                    record={alarmItem}
                    hotels={hotelListRes}
                    alarmType={ALARM_TYPE}
                    alarmPriority={ALARM_PRIORITY}
                />
            )}

            {isShowHistory && (
                <AlarmHistoryModal
                    visible={isShowHistory}
                    onClose={closeHistoryModal}
                    data={{ historyData, totalAlarmsHistory, hotelListRes }}
                />
            )}
        </Card>
    );
};

export { Alarms };
